import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const OperationsAndDiscountsStep = ({ formData, updateFormData, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});

  const daysOfWeek = [
    { key: 'monday', label: 'Lunes' },
    { key: 'tuesday', label: 'Martes' },
    { key: 'wednesday', label: 'Miércoles' },
    { key: 'thursday', label: 'Jueves' },
    { key: 'friday', label: 'Viernes' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' }
  ];

  const updateOperatingHours = (day, field, value) => {
    updateFormData({
      operatingHours: {
        ...formData?.operatingHours,
        [day]: {
          ...formData?.operatingHours?.[day],
          [field]: value
        }
      }
    });
  };

  const copyToAllDays = (sourceDay) => {
    const sourceHours = formData?.operatingHours?.[sourceDay];
    const newHours = {};
    
    daysOfWeek?.forEach(day => {
      newHours[day?.key] = { ...sourceHours };
    });

    updateFormData({ operatingHours: newHours });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.discountPercentage) {
      newErrors.discountPercentage = 'El porcentaje de descuento es requerido';
    } else {
      const percentage = parseFloat(formData?.discountPercentage);
      if (percentage < 5 || percentage > 50) {
        newErrors.discountPercentage = 'El descuento debe estar entre 5% y 50%';
      }
    }

    if (!formData?.discountDescription?.trim()) {
      newErrors.discountDescription = 'La descripción del descuento es requerida';
    }

    if (!formData?.discountTerms?.trim()) {
      newErrors.discountTerms = 'Los términos y condiciones son requeridos';
    }

    if (!formData?.discountValidUntil) {
      newErrors.discountValidUntil = 'La fecha de vencimiento es requerida';
    } else {
      const selectedDate = new Date(formData?.discountValidUntil);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.discountValidUntil = 'La fecha debe ser futura';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Horarios y Descuentos</h2>
        <p className="text-muted-foreground">Configura tus horarios de atención y ofertas para clientes</p>
      </div>
      {/* Operating Hours */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
          <Icon name="Clock" size={20} className="text-accent" />
          Horarios de Atención
        </h3>

        <div className="space-y-3">
          {daysOfWeek?.map((day) => (
            <div key={day?.key} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="w-24">
                <span className="font-semibold text-foreground">{day?.label}</span>
              </div>

              <div className="flex items-center gap-2 flex-1">
                <Input
                  type="checkbox"
                  checked={formData?.operatingHours?.[day?.key]?.closed || false}
                  onChange={(e) => updateOperatingHours(day?.key, 'closed', e?.target?.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-muted-foreground">Cerrado</span>
              </div>

              {!formData?.operatingHours?.[day?.key]?.closed && (
                <>
                  <Input
                    type="time"
                    value={formData?.operatingHours?.[day?.key]?.open || '09:00'}
                    onChange={(e) => updateOperatingHours(day?.key, 'open', e?.target?.value)}
                    className="w-32"
                  />
                  <span className="text-muted-foreground">a</span>
                  <Input
                    type="time"
                    value={formData?.operatingHours?.[day?.key]?.close || '18:00'}
                    onChange={(e) => updateOperatingHours(day?.key, 'close', e?.target?.value)}
                    className="w-32"
                  />
                  {day?.key === 'monday' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => copyToAllDays('monday')}
                      iconName="Copy"
                      className="ml-2"
                    >
                      Copiar a todos
                    </Button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Discount Configuration */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Tag" size={20} className="text-accent" />
          Configuración de Descuentos
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Input
              label="Porcentaje de Descuento"
              type="number"
              required
              min="5"
              max="50"
              step="1"
              placeholder="15"
              value={formData?.discountPercentage || ''}
              onChange={(e) => updateFormData({ discountPercentage: e?.target?.value })}
              error={errors?.discountPercentage}
              description="Entre 5% y 50%"
            />
            {formData?.discountPercentage && (
              <div className="mt-3 bg-green-50 border-2 border-green-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-900">{formData?.discountPercentage}% OFF</p>
                <p className="text-sm text-green-700">Vista previa del descuento</p>
              </div>
            )}
          </div>

          <Input
            label="Válido Hasta"
            type="date"
            required
            value={formData?.discountValidUntil || ''}
            onChange={(e) => updateFormData({ discountValidUntil: e?.target?.value })}
            error={errors?.discountValidUntil}
            min={new Date()?.toISOString()?.split('T')?.[0]}
            description="Fecha de vencimiento del descuento"
          />
        </div>

        <div className="space-y-2 mt-6">
          <label className="text-sm font-medium text-foreground">
            Descripción del Descuento <span className="text-destructive">*</span>
          </label>
          <textarea
            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            placeholder="Ej: Descuento aplicable en todos nuestros productos de la temporada actual..."
            value={formData?.discountDescription || ''}
            onChange={(e) => updateFormData({ discountDescription: e?.target?.value })}
            maxLength={200}
          />
          <div className="flex justify-between items-center text-xs">
            {errors?.discountDescription ? (
              <p className="text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {errors?.discountDescription}
              </p>
            ) : (
              <p className="text-muted-foreground">Describe en qué productos/servicios aplica</p>
            )}
            <p className="text-muted-foreground">
              {formData?.discountDescription?.length || 0}/200
            </p>
          </div>
        </div>

        <div className="space-y-2 mt-6">
          <label className="text-sm font-medium text-foreground">
            Términos y Condiciones <span className="text-destructive">*</span>
          </label>
          <textarea
            className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            placeholder="Ej: No acumulable con otras promociones. Válido solo presentando código QR de BIZZNET..."
            value={formData?.discountTerms || ''}
            onChange={(e) => updateFormData({ discountTerms: e?.target?.value })}
            maxLength={300}
          />
          <div className="flex justify-between items-center text-xs">
            {errors?.discountTerms ? (
              <p className="text-destructive flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />
                {errors?.discountTerms}
              </p>
            ) : (
              <p className="text-muted-foreground">Condiciones de aplicación del descuento</p>
            )}
            <p className="text-muted-foreground">
              {formData?.discountTerms?.length || 0}/300
            </p>
          </div>
        </div>
      </div>
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Estrategia de descuentos</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Los descuentos entre 15-25% generan más conversiones</li>
              <li>Define claramente las exclusiones en los términos</li>
              <li>Puedes modificar los descuentos en cualquier momento desde tu panel</li>
              <li>Los horarios flexibles atraen más clientes</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex gap-4 pt-6 border-t">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
        >
          Regresar
        </Button>

        <Button
          type="submit"
          variant="default"
          size="lg"
          iconName="ArrowRight"
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-accent to-orange-600 hover:shadow-lg transform hover:scale-105 transition-all"
        >
          Continuar
        </Button>
      </div>
    </form>
  );
};

export default OperationsAndDiscountsStep;